<template>
    <div>
        <div class="row">
            <div class="col-md-7 mb-3">
                <input class="form-control custom-green-input-bg" :placeholder="`${$t('Search')}...`" type="text" v-model="term"/>
            </div>
            <div class="col-md-5">
                <app-button v-if="term" native-type="button" type="warning" @click="cancelFilter" class="mt-1" small>
                    {{ $t('Cancel') }}
                </app-button>
                <app-loader-gif class="mt-2" v-if="loading"/>
            </div>
        </div>
        <div v-if="!loading" class="table-responsive">
            <table class="table table-striped table-sm">
                <thead>
                    <tr>
                        <th>#</th>
                        <th @click="orderBy('make.name')" class="pointer">
                            {{ $t('Make') }}
                            <span v-html="order_field === 'make.name' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th @click="orderBy('model.name')" class="pointer">
                            {{ $t('Model') }}
                            <span v-html="order_field === 'model.name' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th>{{ $t('Reg no') }}</th>
                        <th>{{ $t('Year') }}</th>
                        <th @click="orderBy('transmission.name')" class="pointer">
                            {{ $t('Transmission') }}
                            <span v-html="order_field === 'transmission.name' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th @click="orderBy('type.name')" class="pointer">
                            {{ $t('Type') }}
                            <span v-html="order_field === 'type.name' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th @click="orderBy(`group.name[${locale}]`)" class="pointer">
                            {{ $t('Group') }}
                            <span v-html="order_field === `group.name[${locale}]` ? active_sort_icon : sort_icon"/>
                        </th>
                        <th @click="orderBy(`color[${locale}]`)" class="pointer">
                            {{ $t('Color') }}
                            <span v-html="order_field === `color[${locale}]` ? active_sort_icon : sort_icon"/>
                        </th>
                        <th>{{ $t('Images') }}</th>
                        <th @click="orderBy('active')" class="pointer">
                            {{ $t('Active') }}
                            <span v-html="order_field === 'active' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <app-draggable v-model="reorderedVehicles" v-bind="{ animation: 200 }" tag="tbody">
                    <tr v-for="(vehicle, i) in reorderedVehicles" :key="i">
                        <td>{{ i + 1 }}</td>
                        <td style="width: 4%">
                            {{ vehicle.make.name }}
                            <br v-if="vehicle.imgs.length">
                            <img
                                v-if="vehicle.imgs.length"
                                :src="`${envUrl}/vehicles/${vehicle.imgs[0].dbname}`"
                                height="auto"
                                class="img-thumbnail rounded z-depth-1 w-100"/>
                        </td>
                        <td>{{ vehicle.model.name }} <app-vehicle-no :slug="vehicle.slug"/></td>
                        <td>{{ vehicle.reg_no }}</td>
                        <td>
                            {{ vehicle.year }} <br>
                            <small>
                                {{ vehicle.user_year }}
                            </small>
                        </td>
                        <td>{{ $t(vehicle.transmission.name) }}</td>
                        <td>{{ $t(vehicle.type.name) }}</td>
                        <td>{{ vehicle.group ? vehicle.group.name[locale] : '' }}</td>
                        <td>{{ vehicle.color[locale] }}</td>
                        <td>{{ vehicle.imgs.length }}</td>
                        <td>
                            <div v-if="$store.getters.canEditVehicles" class="custom-control custom-switch">
                                <input type="checkbox"
                                       class="custom-control-input"
                                       :id="`active_${i}`"
                                       :checked="vehicle.active || ''"
                                       @change="toggleActive(vehicle.id)">
                                <label class="custom-control-label" :for="`active_${i}`"/>
                            </div>
                        </td>
                        <td class="p-0">
                            <div class="text-right">
                                <router-link v-if="!vehicle.is_empty" :to="{name:'booking.create', query: {vehicle: vehicle.id}}" class="btn btn-sm btn-success text-white mr-2">
                                    {{ $t('Book') }}
                                </router-link>
                                <router-link :to="{name: 'vehicles.show', params: {vehicle_id: vehicle.id}}" class="btn btn-info btn-sm text-white">
                                    {{ $t('View') }}
                                </router-link>
                                <router-link v-if="$store.getters.canEditVehicles" :to="{name: 'vehicles.edit', params: {vehicle_id: vehicle.id}}" class="btn btn-primary btn-sm text-white ml-2">
                                    {{ $t('Edit') }}
                                </router-link>
                                <app-button v-if="$store.getters.canDeleteVehicles" native-type="button" type="danger" @click="destroy(vehicle.id)" small>
                                    {{ $t('Delete') }}
                                </app-button>
                            </div>
                        </td>
                    </tr>
                </app-draggable>
            </table>
        </div>
    </div>
</template>

<script>
import VehicleService from '@services/vehicle.service';
import {mapGetters} from 'vuex';
import appDraggable from 'vuedraggable';
import orderBy from 'lodash/orderBy';

export default {
    data() {
        return {
            loading: true,
            term: '',
            order_field: 'order_no',
            order_dir: 'asc',
            sort_icon: '<span class="mdi mdi-18px mdi-menu-swap"/>',
            vehicles: []
        };
    },
    computed: {
        ...mapGetters(['locale', 'envUrl']),
        filteredVehicles() {
            if (this.vehicles.length) {
                return this.vehicles.filter(vehicle => {
                    const term = this.term.toLowerCase();
                    return (
                        vehicle.reg_no.toLowerCase().match(term) ||
                        vehicle.make.name.toLowerCase().match(term) ||
                        vehicle.model.name.toLowerCase().match(term) ||
                        this.$t(vehicle.transmission.name).toLowerCase().match(term) ||
                        this.$t(vehicle.type.name).toLowerCase().match(term)
                    );
                });
            }
            return false;
        },
        reorderedVehicles: {
            get() {
                return this.orderedVehicles;
            },
            set(vehicles) {
                VehicleService.reorder(vehicles).then(res => {
                    this.$toastr.s(this.$t(res.message));
                    this.getVehicles();
                });
            }
        },
        orderedVehicles() {
            return orderBy(this.filteredVehicles, this.order_field, this.order_dir);
        },
        active_sort_icon() {
            if (this.order_dir === 'desc') {
                return '<span class="mdi mdi-18px mdi-menu-down"/>';
            } else {
                return '<span class="mdi mdi-18px mdi-menu-up"/>';
            }
        }
    },
    methods: {
        toggleActive(vehicleId) {
            VehicleService.toggleActive(vehicleId).then(res => {
                const vehicle = this.vehicles.find(veh => veh.id === vehicleId);
                vehicle.active = !vehicle.active;
            });
        },
        orderBy(field) {
            this.order_field = field;
            if (this.order_dir === 'desc') {
                this.order_dir = 'asc';
            } else {
                this.order_dir = 'desc';
            }
        },
        cancelFilter() {
            this.term = '';
        },
        getVehicles() {
            this.loading = true;
            VehicleService.vehicles().then(res => {
                this.vehicles = res;
                this.loading = false;
            });
        },
        destroy(vehicleId) {
            if(!window.confirm(`${this.$t('Delete')}?`)) {
                return false;
            }
            VehicleService.destroy(vehicleId).then(res => {
                this.getVehicles();
                this.$toastr.s(this.$t('Deleted'));
            });
        }
    },
    components: {
        appDraggable
    },
    created() {
        this.getVehicles();
    }
};
</script>
